import { Component, OnInit, Input, TemplateRef, ViewChild, HostListener } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Client } from '../../client';
import { Company } from '../../company';
import { ClientService } from '../../services/client/client.service';
import { CommonService } from '../../services/common/common.service';
import { CompanyService } from '../../services/company/company.service';
import { Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { NgForm } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

import Swal from 'sweetalert2';
import { AGENCY_TYPES, DEFAULT_STATE, agencyName } from '../../../agency/agency-form';
import { intakeStep, IntakeStep } from '../../intake-steps';

@Component({
    selector: 'app-contact-picker',
    templateUrl: './contact-picker.component.html',
    providers: [ClientService],
    styleUrls: ['./contact-picker.component.scss']
})
export class ContactPickerComponent implements OnInit {
    // Keep in step with $history-panel-width in the stylesheet.
    static readonly HISTORY_PANEL_WIDTH = 280;

    @Input() intakeTab;
    // The tab saving moves on to (handleSubmit), for the mockup "Continue to …" button.
    get nextStep(): IntakeStep { return intakeStep('client', 1); }
    @ViewChild('contactForm') contactForm: NgForm;
    @ViewChild('inspectorsModal') templateRef: TemplateRef<any>;
    @ViewChild('agencyModal') agencyModalRef: TemplateRef<any>;
    private _modalRef: NgbModalRef;
    private _agencyModalRef: NgbModalRef;

    // True until we know whether this role already has a contact, so the search
    // UI never flashes up before the attached-contact card replaces it.
    loading = false;
    // 'search' browses existing contacts, 'create' is the new-contact form.
    step: 'search' | 'create' = 'search';
    // True once a contact is linked to this role; drives the summary card.
    attached = false;
    // Set when the attached contact was opened for editing.
    editingExisting = false;

    job_id = null;
    active_tab: any;
    states: string[];
    lead: string[];

    client = new Client(null, null, null, null, null, null, null, null, null, null, null, null, null, null, 'no', 0, null, false);
    company = new Company(null, null, null, null, null, null, null, null, null, null, null);

    // Snapshot of the contact as stored, so we can tell whether the user edited it.
    originalContact: any = null;
    // Its agency link as stored: the link lives on the contact, so changing only the
    // agency is a contact edit too (it was saved without asking, and so never linked).
    originalAgency: any = null;
    readonly agencyTypes = AGENCY_TYPES;
    // Create New Agency pop-up: set when Save Agency is pressed with neither name nor title.
    agencySubmitted = false;
    // The raw record last applied, so Change can be backed out of.
    appliedContact: any = null;
    previousContact: any = null;
    // VIP flag of the attached contact, kept separately so the summary card can
    // show the badge whether the contact was searched for or loaded from the job.
    contactVip = 0;
    contactAlert = 0;
    // Role settings as loaded, so the unsaved-changes warning covers them too.
    originalRole: any = null;
    // The contact id the server currently holds for this role, and whether a
    // freshly created contact has already been saved. isDirty() compared only
    // field values, so simply ATTACHING a contact - the whole point of this
    // screen - did not register as a change and the tab switch skipped saving.
    private savedClientId: any = null;
    private savedNewContact = false;

    // --- search step ---
    searchTerm = '';
    searchResults = [];
    searching = false;
    searchFailed = false;
    hasSearched = false;
    private searchInput = new Subject<string>();

    // --- duplicate warnings on the create step ---
    // A hit on any of these blocks creation and red-marks that field.
    // Every matching contact per field, not just the first, so the user can
    // see all the records that already look like this one.
    duplicates: any = { email: [], name: [], phone: [] };
    duplicateTotals: any = { email: 0, name: 0, phone: 0 };
    checkingDuplicate = false;

    // Placement fallbacks must be passed as real arrays: ngb only splits an
    // array, and hands a space-separated string straight to removeClass(),
    // which throws on the space. Held as fields so the binding is stable.
    tooltipPlacement: any = ['bottom', 'top', 'left'];

    // --- order history panel ---
    // Positioned with plain CSS against a relative wrapper rather than through
    // ngbPopover, whose coordinate maths lands it at the far left in this shell.
    // Holds the contact whose panel is open, so identity comparison drives it.
    historyOpenFor: any = null;
    historyContact: any = null;
    historyRows = [];
    historyLoading = false;
    // Opens rightwards; flipped only when that would run off the screen.
    historyFlip = false;

    // --- agency ---
    agencyModel: any = null;
    assoc_agency = true;
    newAgency = false;

    // --- misc ---
    prUser: any = [];
    preferredInspectors = [];
    saveStatus = '';
    submitted = false;
    remove_success = false;

    constructor(
        private clientService: ClientService,
        private commonService: CommonService,
        private companyService: CompanyService,
        private _router: Router,
        private router: ActivatedRoute,
        private modalService: NgbModal
    ) { }

    ngOnInit() {
        this.active_tab = this.intakeTab.activeId;
        this.getStates();
        this.getLead();

        this.searchInput.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            tap(() => { this.searching = true; this.hasSearched = true; }),
            switchMap(term => this.clientService.searchContacts(term).pipe(
                tap(() => this.searchFailed = false),
                catchError(() => {
                    this.searchFailed = true;
                    return of([]);
                })
            )),
            tap(() => this.searching = false)
        ).subscribe(results => this.searchResults = (results as any) || []);

        this.router.params.subscribe(params => {
            if (params.jobId) {
                this.job_id = params.jobId;
                this.loading = true;
                this.loadAttachedContact(this.job_id);
            }
        });
    }

    // Loads whichever contact is already linked to this role on the job.
    loadAttachedContact(jobId) {
        this.clientService.getOrder(jobId, this.intakeTab.activeId).subscribe(
            data => {
                if (data && data.client) {
                    this.applyContact(data.client, false);
                    this.client.billable_user = data.billable_user;
                    this.client.lead_id = data.lead_id;
                    this.client.is_client = (data.client_id === data.client.client_id);
                    this.attached = true;
                    this.originalRole = {
                        billable_user: this.client.billable_user,
                        lead_id: this.client.lead_id,
                        is_client: this.client.is_client
                    };
                    this.savedClientId = data.client.client_id;
                    this.savedNewContact = false;
                } else {
                    this.attached = false;
                    this.step = 'search';
                    this.savedClientId = null;
                    this.savedNewContact = false;
                }
                this.loading = false;
            },
            () => {
                // Fall back to the search step rather than stranding a spinner.
                this.attached = false;
                this.step = 'search';
                this.loading = false;
            }
        );
    }

    // Copies a contact record into the form and snapshots it for change detection.
    applyContact(data, openPreferences: boolean) {
        this.appliedContact = data;
        this.contactVip = Number(data.vip) || 0;
        this.contactAlert = Number(data.client_alert) || 0;
        this.client = new Client(
            data.client_id,
            data.client_first_name,
            data.client_last_name,
            data.client_notes,
            data.client_phone,
            data.client_email,
            data.client_address1,
            data.client_address2,
            data.client_city,
            data.client_state,
            data.client_zip,
            (data.client_is_company == 1) ? 'yes' : 'no',
            null,
            null,
            this.client.billable_user || 'no',
            this.client.lead_id || 0,
            null,
            this.client.is_client
        );

        if (data.company && data.company.company_id) {
            this.company = new Company(
                data.company.company_id,
                data.company.company_id,
                data.company.company_reference_id,
                data.company.company_type,
                data.company.company_name,
                data.company.company_address1,
                data.company.company_address2,
                data.company.company_city,
                data.company.company_state,
                data.company.company_zip,
                data.company.company_phone,
                data.company.company_title
            );
            this.agencyModel = { company_name: data.company.company_name || data.company.company_title };
            this.client.is_company = 'yes';
            this.assoc_agency = false;
        } else {
            this.clearAgency();
        }

        this.originalContact = {
            client_first_name: data.client_first_name,
            client_last_name: data.client_last_name,
            client_notes: data.client_notes,
            client_phone: data.client_phone,
            client_email: data.client_email,
            client_address1: data.client_address1,
            client_address2: data.client_address2,
            client_city: data.client_city,
            client_state: data.client_state,
            client_zip: data.client_zip
        };
        this.originalAgency = this.agencySnapshot();

        if (openPreferences) {
            this.showPreferences(data);
        }
    }

    // The billable flag is stored as 'yes'/'no', the toggle wants a boolean.
    get billableToggle(): boolean {
        return this.client.billable_user === 'yes';
    }
    set billableToggle(value: boolean) {
        this.client.billable_user = value ? 'yes' : 'no';
    }

    // VIP / preferred-inspector modal, unchanged from the old client screen.
    showPreferences(data) {
        this.prUser = data;
        this.clientService.getPreferredInspectors(data.client_id).subscribe(inspectors => {
            const vip = String(this.prUser.vip) === '1';
            this.preferredInspectors = (inspectors && inspectors.length > 0) ? inspectors : [];
            if (this.preferredInspectors.length === 0 && !vip && !this.prUser.client_notes) {
                return;
            }
            // Picking a second contact quickly must not stack modals.
            this.closeModal();
            this._modalRef = this.modalService.open(this.templateRef, {
                size: 'lg',
                windowClass: vip ? 'flashing-border' : ''
            });
        });
    }

    closeModal() {
        if (this._modalRef) {
            this._modalRef.close();
            this._modalRef = null;
        }
    }

    // ------------------------------------------------------- order history

    // Opens the small history panel next to the magnifier that was clicked.
    toggleHistory(contact, event) {
        // Result rows are clickable, so the magnifier must not also select them.
        // It also keeps the document handler below from closing us immediately.
        if (event) {
            event.stopPropagation();
        }

        if (this.historyOpenFor === contact) {
            this.historyOpenFor = null;
            return;
        }

        // The magnifier sits near the right edge of the table, so on narrower
        // screens there is not always room for the panel beside it.
        if (event && event.currentTarget && event.currentTarget.getBoundingClientRect) {
            const rect = event.currentTarget.getBoundingClientRect();
            const needed = ContactPickerComponent.HISTORY_PANEL_WIDTH + 12;
            this.historyFlip = (rect.right + needed) > document.documentElement.clientWidth;
        } else {
            this.historyFlip = false;
        }

        this.historyOpenFor = contact;
        this.historyContact = contact;
        this.historyRows = [];
        this.historyLoading = true;

        this.clientService.getContactHistory(contact.client_id).subscribe(
            rows => {
                this.historyRows = (rows as any) || [];
                this.historyLoading = false;
            },
            () => {
                this.historyRows = [];
                this.historyLoading = false;
            }
        );
    }

    closeHistory(event?) {
        // Must stop here, synchronously. Clearing historyOpenFor makes *ngIf
        // destroy this view, and Angular unbinds the panel's own stopPropagation
        // listener along with it — so the click would carry on up to the row and
        // select that contact. The onRowClick guard cannot help either, because
        // by then historyOpenFor is already null.
        if (event) {
            event.stopPropagation();
        }
        this.historyOpenFor = null;
    }

    // Any click that is not on the panel or its magnifier dismisses it — both of
    // those stop propagation, so this never sees them.
    @HostListener('document:click')
    onDocumentClick() {
        this.historyOpenFor = null;
    }

    // A click on the row while a history panel is open is the user dismissing
    // that panel, not picking the contact. Without this, closing the panel by
    // clicking away silently attached whichever row was underneath.
    onRowClick(contact) {
        if (this.historyOpenFor) {
            this.historyOpenFor = null;
            return;
        }
        this.selectContact(contact);
    }

    historyName(contact): string {
        if (!contact) {
            return '';
        }
        return contact.full_name
            || (contact.client_first_name + ' ' + contact.client_last_name).trim();
    }

    // ---------------------------------------------------------------- search

    onSearchInput(term: string) {
        this.searchTerm = term;
        if (!term || !term.trim()) {
            this.searchResults = [];
            this.hasSearched = false;
            this.searching = false;
            return;
        }
        this.searchInput.next(term);
    }

    selectContact(contact) {
        this.applyContact(contact, true);
        this.previousContact = null;
        this.attached = true;
        this.editingExisting = false;
        this.step = 'search';
        this.clearDuplicates();
    }

    // ---------------------------------------------------------------- create

    startCreate() {
        // Toggling back and forth must not wipe what the user has typed.
        if (this.step === 'create') {
            return;
        }
        this.step = 'create';
        this.editingExisting = false;
        this.attached = false;
        this.originalContact = null;
        this.contactVip = 0;
        this.contactAlert = 0;
        this.clearDuplicates();
        this.client = new Client(
            null, null, null, null, null, null, null, null, null, null, null,
            'no', null, null, this.client.billable_user || 'no', this.client.lead_id || 0, null, this.client.is_client
        );
        this.client.client_state = DEFAULT_STATE;
        this.clearAgency();
    }

    goToSearch() {
        this.step = 'search';
        this.clearDuplicates();
    }

    // Opens the attached contact in the form for editing. This is not part of
    // the two-step pick, so the step control is hidden while it is on.
    editContact() {
        this.attached = false;
        this.editingExisting = true;
        this.step = 'create';
        this.clearDuplicates();
    }

    cancelEdit() {
        this.restoreOriginalContact();
        this.editingExisting = false;
        this.attached = true;
        this.step = 'search';
        this.clearDuplicates();
    }

    // Detaches the contact so a different one can be picked. The job still points
    // at the old contact until Save, so this is reversible until then.
    changeContact() {
        this.previousContact = this.attached ? this.appliedContact : null;
        this.attached = false;
        this.editingExisting = false;
        this.step = 'search';
        this.originalContact = null;
        this.appliedContact = null;
        this.contactVip = 0;
        this.contactAlert = 0;
        this.searchTerm = '';
        this.searchResults = [];
        this.hasSearched = false;
        this.clearDuplicates();
        this.client = new Client(
            null, null, null, null, null, null, null, null, null, null, null,
            'no', null, null, this.client.billable_user || 'no', this.client.lead_id || 0, null, this.client.is_client
        );
        this.clearAgency();
    }

    // Backs out of a Change and puts the previous contact back.
    cancelChange() {
        if (!this.previousContact) {
            return;
        }
        this.applyContact(this.previousContact, false);
        this.previousContact = null;
        this.attached = true;
        this.editingExisting = false;
        this.step = 'search';
        this.clearDuplicates();
    }

    // Nothing to save until a contact is picked or a new one has a name.
    get hasContactSelected(): boolean {
        return !!(this.client.client_id
            || (this.client.client_first_name && this.client.client_last_name));
    }

    removeContact() {
        this.clientService.removeClient(this.job_id, this.active_tab).subscribe(data => {
            if (data) {
                this.changeContact();
                this.savedClientId = null;
                this.savedNewContact = false;
                this.remove_success = true;
                setTimeout(() => this.remove_success = false, 5000);
            }
        });
    }

    clearDuplicates() {
        this.duplicates = { email: [], name: [], phone: [] };
        this.duplicateTotals = { email: 0, name: 0, phone: 0 };
    }

    // Only a repeated email address stops a new contact being created.
    // Two people genuinely share a name, and a whole office shares one phone
    // number, so those are surfaced but never block the save.
    get hasBlockingDuplicate(): boolean {
        return this.duplicates.email.length > 0;
    }

    get hasAnyDuplicate(): boolean {
        return this.duplicates.email.length > 0
            || this.duplicates.name.length > 0
            || this.duplicates.phone.length > 0;
    }

    /** How many matches exist beyond the ones listed. */
    get hiddenDuplicateCount(): number {
        const shown = this.duplicateMatches.length;
        const total = Math.max(
            this.duplicateTotals.email,
            this.duplicateTotals.name,
            this.duplicateTotals.phone
        );
        return total > shown ? total - shown : 0;
    }

    // The distinct contacts behind the flagged fields, for the warning panel.
    get duplicateMatches(): any[] {
        const seen = {};
        const out = [];
        ['email', 'name', 'phone'].forEach(field => {
            (this.duplicates[field] || []).forEach(match => {
                if (match && !seen[match.client_id]) {
                    seen[match.client_id] = true;
                    out.push(match);
                }
            });
        });
        return out;
    }

    // Which fields are flagged, for the message under the warning.
    get duplicateFieldLabels(): string {
        const labels = [];
        if (this.duplicates.name.length) { labels.push('name'); }
        if (this.duplicates.email.length) { labels.push('email address'); }
        if (this.duplicates.phone.length) { labels.push('phone number'); }
        return labels.join(', ').replace(/, ([^,]*)$/, ' and $1');
    }

    // Runs on email / name / phone blur in the create step.
    /**
     * Backs the clear button on a flagged field. Bootstrap paints its own
     * marker on .is-invalid, but that is a background image and cannot be
     * clicked, so each field that can be flagged gets a real button.
     */
    clearField(field: string) {
        this.client[field] = '';
        this.checkDuplicate();
    }

    checkDuplicate() {
        if (this.step !== 'create') {
            return;
        }
        const hasEmail = !!(this.client.client_email && String(this.client.client_email).trim());
        const hasName = !!(this.client.client_first_name && this.client.client_last_name);
        const hasPhone = !!(this.client.client_phone && String(this.client.client_phone).replace(/\D/g, '').length >= 7);
        if (!hasEmail && !hasName && !hasPhone) {
            this.clearDuplicates();
            return;
        }

        this.checkingDuplicate = true;
        this.clientService.checkDuplicate({
            client_id: this.client.client_id,
            client_email: this.client.client_email,
            client_first_name: this.client.client_first_name,
            client_last_name: this.client.client_last_name,
            client_phone: this.client.client_phone
        }).subscribe(
            data => {
                this.checkingDuplicate = false;
                this.duplicates = {
                    email: (data && data.email) || [],
                    name: (data && data.name) || [],
                    phone: (data && data.phone) || []
                };
                this.duplicateTotals = (data && data.totals) || { email: 0, name: 0, phone: 0 };
            },
            () => {
                this.checkingDuplicate = false;
                this.clearDuplicates();
            }
        );
    }

    // "Use this contact" on a duplicate warning.
    useDuplicate(contact) {
        this.selectContact(contact);
    }

    // ----------------------------------------------------------------- save

    clientSubmit() {
        // Without this, an abandoned Change would save a blank contact row.
        if (!this.hasContactSelected) {
            Swal({
                title: 'No contact selected',
                text: 'Pick an existing contact or fill in a new one before saving this party.',
                type: 'warning'
            });
            return;
        }
        // Field-level rules (phone format, and anything added later) were not
        // being consulted here, so a malformed value could still be saved.
        if (this.contactForm && this.contactForm.invalid) {
            Swal({
                title: 'Check these details',
                text: 'Some details are not filled in correctly. Correct the highlighted fields and try again.',
                type: 'warning'
            });
            return;
        }
        // A duplicate on name, email or phone blocks creating another record.
        if (!this.client.client_id && this.hasBlockingDuplicate) {
            Swal({
                title: 'This contact already exists',
                text: 'A contact with the same email address is already in your database.'
                    + ' Use that contact instead, or change the email address.',
                type: 'warning'
            });
            return;
        }
        if (this.hasEmptyAddress()) {
            Swal({
                title: 'Missing Address',
                text: 'The Structural Pest Control Board requires an address for the Owner, Ordered by party, and all that will receive the report. Are you sure you want to continue without an address?',
                type: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Confirm',
            }).then(result => {
                if (result.value) {
                    this.confirmContactUpdate();
                }
            });
        } else {
            this.confirmContactUpdate();
        }
    }

    // A contact is shared across jobs, so editing one has to be deliberate.
    confirmContactUpdate() {
        if (this.client.client_id && this.contactFieldsChanged()) {
            Swal({
                title: 'This contact has been edited',
                text: 'These details belong to a contact that other jobs may also use. Update the contact everywhere, or discard your edits and link it as it is stored?',
                type: 'question',
                showCancelButton: true,
                confirmButtonText: 'Update contact everywhere',
                cancelButtonText: 'Discard my edits',
            }).then(result => {
                if (result.value) {
                    this.handleSubmit(true);
                } else {
                    this.restoreOriginalContact();
                    this.handleSubmit(false);
                }
            });
        } else {
            this.handleSubmit(false);
        }
    }

    handleSubmit(updateContact: boolean) {
        const payload: any = Object.assign({}, this.client, {
            company: this.company,
            active_id: this.active_tab,
            job_id: this.job_id,
            update_contact: updateContact
        });

        this.submitted = true;
        this.clientService.newClientOrder(payload).subscribe(
            data => {
                this.submitted = false;
                this.saveStatus = 'Saved';
                this.editingExisting = false;
                this.job_id = data.job_id;
                this.markSaved();

                const stored = localStorage.getItem('tab_orders');
                const tabsOrder = stored ? JSON.parse(stored) : null;
                if (tabsOrder) {
                    const tabKeys = Object.keys(tabsOrder);
                    const nextTab = tabsOrder[tabKeys[tabKeys.indexOf('client') + 1]];
                    if (nextTab) {
                        this._router.navigate([nextTab + '/', data.job_id]);
                        return;
                    }
                }
                // No next tab configured: stay here and show what was saved.
                this.loadAttachedContact(data.job_id);
            },
            () => {
                this.submitted = false;
            }
        );
    }

    // Puts the stored values back when the user discards their edits.
    restoreOriginalContact() {
        if (!this.originalContact) {
            return;
        }
        const target: any = this.client;
        Object.keys(this.originalContact).forEach(field => {
            target[field] = this.originalContact[field];
        });
        if (this.originalAgency) {
            this.company = Object.assign(new Company(null, null, null, null, null, null, null, null, null, null, null),
                this.originalAgency.company);
            this.client.is_company = this.originalAgency.is_company;
            this.agencyModel = this.client.is_company === 'yes' ? { company_name: this.company.company_name || this.company.company_title } : null;
            this.assoc_agency = this.client.is_company !== 'yes';
        }
    }

    contactFieldsChanged(): boolean {
        if (!this.originalContact) {
            return false;
        }
        const current: any = this.client;
        return Object.keys(this.originalContact).some(
            field => this.normalise(current[field]) !== this.normalise(this.originalContact[field])
        ) || this.agencyChanged();
    }

    private agencySnapshot() {
        return { is_company: this.client.is_company, company: JSON.parse(JSON.stringify(this.company)) };
    }

    private agencyChanged(): boolean {
        if (!this.originalAgency) {
            return false;
        }
        const before = this.originalAgency;
        const linked = (value) => value === 'yes';
        if (linked(this.client.is_company) !== linked(before.is_company)) {
            return true;
        }
        if (!linked(this.client.is_company)) {
            return false;
        }
        return Object.keys(before.company).some(
            field => this.normalise((this.company as any)[field]) !== this.normalise(before.company[field])
        );
    }

    private normalise(value): string {
        return (value === null || value === undefined) ? '' : String(value).trim();
    }

    /**
     * Re-baselines change detection against what was just written.
     *
     * A contact created here has no client_id locally - new_order returns only
     * the job - so it is flagged separately to stop a second save creating the
     * same person again.
     */
    private markSaved() {
        this.savedClientId = this.client.client_id || null;
        this.savedNewContact = !this.client.client_id;
        this.originalRole = {
            billable_user: this.client.billable_user,
            lead_id: this.client.lead_id,
            is_client: this.client.is_client
        };
        if (this.client.client_id) {
            this.originalContact = {
                client_first_name: this.client.client_first_name,
                client_last_name: this.client.client_last_name,
                client_notes: this.client.client_notes,
                client_phone: this.client.client_phone,
                client_email: this.client.client_email,
                client_address1: this.client.client_address1,
                client_address2: this.client.client_address2,
                client_city: this.client.client_city,
                client_state: this.client.client_state,
                client_zip: this.client.client_zip
            };
            this.originalAgency = this.agencySnapshot();
        }
    }

    // Used by the parent tabset to warn before throwing away unsaved work.
    isDirty(): boolean {
        if (this.step === 'create' && !this.client.client_id) {
            // Already written to the server; re-saving would duplicate it.
            if (this.savedNewContact) {
                return false;
            }
            return !!(this.client.client_first_name || this.client.client_last_name || this.client.client_email || this.client.client_phone);
        }
        // A contact is attached that the server does not have for this role yet
        // - either newly picked, or swapped for a different one. Detaching
        // without picking a replacement is deliberately not counted: nothing
        // has been decided, and the stored contact is still the truth.
        if (this.client.client_id
            && this.normalise(this.client.client_id) !== this.normalise(this.savedClientId)) {
            return true;
        }
        return this.contactFieldsChanged() || this.roleFieldsChanged();
    }

    // ------------------------------------------------- switching away

    /**
     * Why this party cannot be saved as it stands, or null when it can.
     *
     * Used when the user switches party tabs: the switch is driven by one
     * dialog and cannot afford to open the chain of prompts clientSubmit()
     * relies on, so the blocking conditions are reported up front instead.
     */
    blockingReason(): string | null {
        if (!this.hasContactSelected) {
            return 'Pick an existing contact or fill in a new one.';
        }
        if (!this.client.client_id && this.hasBlockingDuplicate) {
            return 'A contact with the same email address already exists.';
        }
        if (this.checkingDuplicate) {
            return 'Still checking this contact against existing ones.';
        }
        if (this.contactForm && this.contactForm.invalid) {
            return 'Some details are not filled in correctly.';
        }
        return null;
    }

    /** True when saving would change a contact other jobs share. */
    needsContactUpdateChoice(): boolean {
        return !!(this.client.client_id && this.contactFieldsChanged());
    }

    /**
     * Saves without routing anywhere.
     *
     * handleSubmit() navigates on to the next section, which is right for
     * "Save and continue" but wrong when the user is only moving between
     * parties. Resolves with the job id, which the caller needs when this was
     * the save that brought a new job into existence.
     */
    saveInPlace(updateContact: boolean): Promise<any> {
        const payload: any = Object.assign({}, this.client, {
            company: this.company,
            active_id: this.active_tab,
            job_id: this.job_id,
            update_contact: updateContact
        });

        this.submitted = true;

        return new Promise((resolve, reject) => {
            this.clientService.newClientOrder(payload).subscribe(
                data => {
                    this.submitted = false;
                    this.saveStatus = 'Saved';
                    this.editingExisting = false;
                    this.job_id = data.job_id;
                    this.markSaved();
                    resolve(data);
                },
                error => {
                    this.submitted = false;
                    reject(error);
                }
            );
        });
    }

    private roleFieldsChanged(): boolean {
        if (!this.originalRole) {
            return false;
        }
        return this.normalise(this.client.billable_user) !== this.normalise(this.originalRole.billable_user)
            || this.normalise(this.client.lead_id) !== this.normalise(this.originalRole.lead_id)
            || !!this.client.is_client !== !!this.originalRole.is_client;
    }

    hasEmptyAddress() {
        return (
            this.client.is_company === 'yes'
            && (!this.company.company_address1 || !this.company.company_city || !this.company.company_zip)
        )
        || (!this.client.client_address1 || !this.client.client_city || !this.client.client_zip);
    }

    // --------------------------------------------------------------- lookups

    getLead() {
        this.clientService.getLead().subscribe(data => this.lead = data as string[]);
    }

    getStates() {
        this.commonService.getState().subscribe(data => this.states = data as string[]);
    }

    // ---------------------------------------------------------------- agency

    existCompany(data) {
        if (data) {
            this.company = new Company(
                data.item.company_id,
                data.item.company_id,
                data.item.company_reference_id,
                data.item.company_type,
                data.item.company_name,
                data.item.company_address1,
                data.item.company_address2,
                data.item.company_city,
                data.item.company_state,
                data.item.company_zip,
                data.item.company_phone,
                data.item.company_title
            );
            this.agencyModel = data.item;
            this.client.is_company = 'yes';
            this.assoc_agency = false;
            this.newAgency = false;
        }
    }

    onAgencyInput(event) {
        if (!event.target.value) {
            this.clearAgency();
        }
    }

    addNewAgency() {
        this.company = new Company(null, null, null, null, null, null, null, null, null, null, null);
        this.client.is_company = 'yes';
        this.assoc_agency = false;
        this.newAgency = true;
    }

    openAgencyModal(isNew) {
        // What was typed in the agency search (nothing picked) becomes the new office name.
        const typed = typeof this.agencyModel === 'string' ? this.agencyModel.trim() : '';
        if (isNew) {
            this.addNewAgency();
            this.company.company_name = typed || null;
            this.company.company_state = DEFAULT_STATE;
        } else {
            this.newAgency = false;
        }
        this.agencySubmitted = false;
        // stored as '' when never set; null shows "Select agency type"
        this.company.company_type = this.company.company_type || null;
        const snapshot = JSON.parse(JSON.stringify(this.company));
        this._agencyModalRef = this.modalService.open(this.agencyModalRef, { size: 'lg' });
        this._agencyModalRef.result.then(
            () => {
                this.client.is_company = 'yes';
                this.assoc_agency = false;
                this.agencyModel = { company_name: this.company.company_name || this.company.company_title };
            },
            () => {
                Object.assign(this.company, snapshot);
                if (!agencyName(this.company)) {
                    this.clearAgency();
                } else {
                    this.agencyModel = { company_name: this.company.company_name || this.company.company_title };
                }
            }
        );
    }

    // Save Agency: an agency needs an office name or a title (as the Add Agency page).
    saveAgencyModal(modal) {
        this.agencySubmitted = true;
        if (agencyName(this.company)) {
            modal.close();
        }
    }

    clearAgency() {
        this.company = new Company(null, null, null, null, null, null, null, null, null, null, null);
        this.client.is_company = 'no';
        this.assoc_agency = true;
        this.newAgency = false;
        this.agencyModel = null;
    }

    companySearch = (text$: Observable<string>) =>
        text$.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            switchMap(term =>
                this.companyService.searchCompany(term).pipe(
                    catchError(() => of([]))
                )
            )
        )
    companyFormatter = (x: { company_name: string, company_title?: string }) => x.company_name || x.company_title;
}
